import { Upload } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { toZonedDateTime } from '@ms-rewards/date-lib';
import { WorkoutTypeV2 } from '@api/FitnessActivityClient';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { DateTimePicker } from '@components/date-picker';
import { Option, Select } from '@components/select';
import { TextField } from '@components/textfield';
import { getTimezoneOptions } from '@components/utils/time';
import { injectTSDI } from '../../../../../core/tsdi';
import { I18n } from '../../../../../core/i18n';
import { useStore } from './store';

interface AddWorkoutProps {
    open?: boolean;
    onModalClose?(): void;
    onSuccess?: () => void;
}

const WORKOUT_TYPES: Option<WorkoutTypeV2>[] = Object.values(WorkoutTypeV2)
    .map((type) => {
        return { value: type, label: type.toString() };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

export const AddWorkout = observer(
    ({
        open: initialOpen = false,
        onModalClose,
        onSuccess
    }: AddWorkoutProps) => {
        const [open, setOpen] = useState(initialOpen);
        const {
            fields,
            errors,
            onSubmitHandler,
            addWorkoutRequest,
            gpxFile,
            gpxError,
            applyGpxFile,
            clearGpxFile
        } = useStore(() => {
            handleClose();
            onSuccess?.();
        });
        const { __ } = injectTSDI(I18n);

        const {
            workoutType,
            from,
            to,
            distance,
            timeZone,
            averageSpeed,
            elevation,
            caloriesBurned
        } = fields;

        const handleClose = () => {
            setOpen(false);
            onModalClose?.();
        };

        useEffect(() => {
            setOpen(initialOpen);
        }, [initialOpen]);

        const handleSubmit = async () => {
            await onSubmitHandler();
        };

        const optionalNumber = (val: string | number) =>
            val === '' ? undefined : Number(val);

        return (
            <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>Add Workout</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 md:grid-cols-3">
                        <div className="flex flex-wrap items-center gap-4 md:col-span-3">
                            <input
                                type="file"
                                accept=".gpx"
                                onChange={async (event) => {
                                    const file = event.target.files?.[0];
                                    if (file) {
                                        await applyGpxFile(file);
                                    }
                                    event.target.value = '';
                                }}
                                className="hidden"
                                id="add-workout-gpx-input"
                            />
                            <Button variant="outline" asChild>
                                <label htmlFor="add-workout-gpx-input">
                                    <Upload />
                                    {gpxFile
                                        ? 'Change GPX file'
                                        : 'Attach GPX file'}
                                </label>
                            </Button>
                            {gpxFile && (
                                <>
                                    <span className="text-sm">
                                        {gpxFile.name}
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={clearGpxFile}
                                    >
                                        Remove
                                    </Button>
                                </>
                            )}
                            {gpxError && (
                                <span className="text-sm text-destructive">
                                    {gpxError}
                                </span>
                            )}
                        </div>
                        <DateTimePicker
                            label="Start date"
                            value={from.value ? from.value.toDate() : null}
                            onChange={(value) => {
                                if (value == null) {
                                    return;
                                }
                                void from.onChange(
                                    toZonedDateTime(value, timeZone.value)
                                );
                            }}
                        />
                        <DateTimePicker
                            label="End date"
                            value={to.value ? to.value.toDate() : null}
                            onChange={(value) => {
                                if (value == null) {
                                    return;
                                }
                                void to.onChange(
                                    toZonedDateTime(value, timeZone.value)
                                );
                            }}
                            minDate={
                                from.value ? from.value.toDate() : undefined
                            }
                        />
                        <Select
                            label="Timezone"
                            value={timeZone.value}
                            options={getTimezoneOptions()}
                            onChange={(value) => {
                                timeZone.onChange(value);
                            }}
                        />
                        <Select
                            label="Workout type"
                            value={workoutType.value}
                            onChange={workoutType.onChange}
                            options={WORKOUT_TYPES}
                        />
                        <div className="md:col-span-2">
                            <TextField
                                value={distance.value}
                                onChange={distance.onChange}
                                type="number"
                                label="Distance traveled (in meters)"
                                required
                                error={
                                    distance.error &&
                                    __(distance.error as I18nKey, 0)
                                }
                            />
                        </div>
                        <TextField
                            value={averageSpeed.value ?? ''}
                            onChange={(val) =>
                                averageSpeed.onChange(optionalNumber(val))
                            }
                            type="number"
                            label="Average speed (in m/s, optional)"
                            error={
                                averageSpeed.error &&
                                __(averageSpeed.error as I18nKey, 0)
                            }
                        />
                        <TextField
                            value={elevation.value ?? ''}
                            onChange={(val) =>
                                elevation.onChange(optionalNumber(val))
                            }
                            type="number"
                            label="Elevation gain (in meters, optional)"
                            error={
                                elevation.error &&
                                __(elevation.error as I18nKey, 0)
                            }
                        />
                        <TextField
                            value={caloriesBurned.value ?? ''}
                            onChange={(val) =>
                                caloriesBurned.onChange(optionalNumber(val))
                            }
                            type="number"
                            label="Calories burned (optional)"
                            error={
                                caloriesBurned.error &&
                                __(caloriesBurned.error as I18nKey, 0)
                            }
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={handleClose}
                            disabled={addWorkoutRequest.loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            loading={addWorkoutRequest.loading}
                            disabled={Boolean(errors)}
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }
);
